import { getPreferredUnits } from "../Services/Global";
import { Constants } from "../Models/Constants";

const POUNDS_TO_KG_FACTOR = 0.45359237;
const FAHRENHEIT_TO_CELSIUS_FACTOR = 5 / 9;
const CELSIUS_TO_FAHRENHEIT_FACTOR = 1.8;
const M3H_TO_GPM_FACTOR = 4.402867539;
const KPA_TO_PSI_FACTOR = 0.145037738;
const MS_TO_FPS_FACTOR = 3.2808399;

export function convertPoundsToKilograms(input: number, round: boolean = true) {
    const val = input * POUNDS_TO_KG_FACTOR;
    return round ? Math.ceil(val) : val;
}

export function convertKilogramsToPounds(input: number, round: boolean = true) {
    const val = input / POUNDS_TO_KG_FACTOR;
    return round ? Math.ceil(val) : val;
}

export function convertFahrenheitToCelsius(input: number, round: boolean = true) {
    const val = (input - 32) * FAHRENHEIT_TO_CELSIUS_FACTOR;
    return round ? Math.ceil(val) : val;
}

export function convertCelsiusToFahrenheit(input: number, round: boolean = false) {
    const val = (input * CELSIUS_TO_FAHRENHEIT_FACTOR) + 32;
    return round ? Math.ceil(val) : val;
}

export function convertM3hToGpm(input: number, round: boolean = false) {
    const val = input * M3H_TO_GPM_FACTOR;
    return round ? Math.ceil(val) : val;
}

export function convertGpmToM3h(input: number, round: boolean = false) {
    const val = input / M3H_TO_GPM_FACTOR;
    return round ? Math.ceil(val) : val;
}

export function convertKpaToPsi(input: number, round: boolean = false) {
    const val = input * KPA_TO_PSI_FACTOR;
    return round ? Math.ceil(val) : val;
}

export function convertPsiToKpa(input: number, round: boolean = false) {
    const val = input / KPA_TO_PSI_FACTOR;
    return round ? Math.ceil(val) : val;
}

export function convertMsToFps(input: number, round: boolean = false) {
    const val = input * MS_TO_FPS_FACTOR;
    return round ? Math.ceil(val) : val;
}

export function convertFpsToMs(input: number, round: boolean = false) {
    const val = input / MS_TO_FPS_FACTOR;
    return round ? Math.ceil(val) : val;
}

export const TemperatureUnit = getPreferredUnits().preferredUnits === 'us' ? '°F' : '°C';
export const MassFlowUnit = getPreferredUnits().preferredUnits === 'us' ? 'lb/h' : 'kg/h';
export const VolumetricFlowUnit = getPreferredUnits().preferredUnits === 'us' ? 'GPM' : 'm3/h';
export const FlowUnit = getPreferredUnits().preferredUnits === 'us' ? 'lb/h' : 'kg/h'; // Keeping for backwards compatibility
export const PressureUnit = getPreferredUnits().preferredUnits === 'us' ? 'PSI' : 'kPa';
export const VelocityUnit = getPreferredUnits().preferredUnits === 'us' ? 'ft/s' : 'm/s';

export type UnitType = 'temperature' | 'massFlow' | 'volumetricFlow' | 'pressure' | 'velocity';

/**
 * Map a configured unit label onto the physical quantity it denotes.
 *
 * Returns null both for labels that have no metric/US distinction ('%',
 * 'Integer') and for unrecognised labels. In either case the value must be
 * passed through unconverted — converting a dimensionless quantity is what
 * corrupted UnitOperationTargetValue for US users.
 */
export function resolveUnitTypeFromLabel(unitLabel?: string | null): UnitType | null {
    if (!unitLabel) return null;

    switch (unitLabel.trim().toLowerCase()) {
        case 'kpa':
        case 'psi':
            return 'pressure';
        case 'kg/h':
        case 'lb/h':
            return 'massFlow';
        case 'm3/h':
        case 'gpm':
            return 'volumetricFlow';
        case '°c':
        case 'c':
        case '°f':
        case 'f':
            return 'temperature';
        case 'm/s':
        case 'ft/s':
            return 'velocity';
        default:
            return null;
    }
}

/**
 * The flow quantity implied by a unit configuration's flowType.
 */
export function resolveFlowUnitType(flowType?: string | null): UnitType {
    return flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow';
}

/**
 * Canonical unit-type resolution for a form property.
 *
 * The unit operation target can carry any quantity (percent, temperature,
 * pressure, or nothing at all), so it is resolved from its configured unit
 * label. Every other property is identifiable from its name, because media
 * property names always contain Temp, Pressure, or a flow term.
 */
export function resolveUnitTypeForProperty(
    propertyName: string,
    unitLabel: string | undefined | null,
    flowType: string | undefined | null
): UnitType | null {
    if (propertyName === Constants.UnitOperationTargetValue) {
        return resolveUnitTypeFromLabel(unitLabel);
    }
    if (propertyName.includes('Pressure')) return 'pressure';
    if (propertyName.includes('Temp')) return 'temperature';
    return resolveFlowUnitType(flowType);
}

/**
 * Number of decimal places used when validating values in the displayed unit.
 * Must match the precision shown to the user (tooltip uses toFixed(2)), so that
 * the boundary the user is told about is exactly the boundary that is validated.
 */
export const VALIDATION_DISPLAY_DECIMALS = 2;

/**
 * Convert a metric value into the unit currently displayed to the user.
 * In metric mode this is the identity; in US mode it applies the matching
 * conversion. No rounding is applied here.
 */
export function metricToDisplay(metricVal: number, unitType: UnitType | null): number {
    if (unitType === null) {
        return metricVal;
    }

    if (getPreferredUnits().preferredUnits !== 'us') {
        return metricVal;
    }

    switch (unitType) {
        case 'temperature': return convertCelsiusToFahrenheit(metricVal, false);
        case 'massFlow': return convertKilogramsToPounds(metricVal, false);
        case 'volumetricFlow': return convertM3hToGpm(metricVal, false);
        case 'pressure': return convertKpaToPsi(metricVal, false);
        case 'velocity': return convertMsToFps(metricVal, false);
        default: return metricVal;
    }
}

/**
 * Round to the given number of decimals using the same method as the display
 * formatting (formatUnitValue uses `parseFloat(x.toFixed(decimals))`), so a
 * value and a boundary that look identical to the user compare as equal.
 */
function roundToDisplayPrecision(value: number, decimals: number): number {
    return parseFloat(value.toFixed(decimals));
}

/**
 * Determine whether a metric value falls outside the allowed range, comparing
 * in the *displayed* unit at the *displayed* precision (Option B).
 *
 * Validating in metric space against exact metric boundaries with a strict
 * comparison wrongly rejected values that the user entered in US units, because
 * the round-trip US -> metric conversion lands microscopically off the boundary
 * (floating-point error) and because the user types fewer decimals than the
 * full-precision converted boundary. Comparing what the user actually sees
 * removes both problems and keeps validation consistent with the tooltip.
 *
 * @param metricValue   The value in metric units (already converted back from the input).
 * @param metricMin     Lower bound in metric units (range.lessThan), or undefined/null if none.
 * @param metricMax     Upper bound in metric units (range.greaterThan), or undefined/null if none.
 * @param unitType      The physical quantity, used to pick the display conversion.
 * @param decimals      Display precision to compare at (defaults to the tooltip precision).
 */
export function isOutOfDisplayRange(
    metricValue: number,
    metricMin: number | undefined | null,
    metricMax: number | undefined | null,
    unitType: UnitType | null,
    decimals: number = VALIDATION_DISPLAY_DECIMALS
): boolean {
    if (metricValue === undefined || metricValue === null || isNaN(metricValue)) {
        return false;
    }

    const displayValue = roundToDisplayPrecision(metricToDisplay(metricValue, unitType), decimals);

    if (metricMin !== undefined && metricMin !== null) {
        const displayMin = roundToDisplayPrecision(metricToDisplay(metricMin, unitType), decimals);
        if (displayValue < displayMin) {
            return true;
        }
    }

    if (metricMax !== undefined && metricMax !== null) {
        const displayMax = roundToDisplayPrecision(metricToDisplay(metricMax, unitType), decimals);
        if (displayValue > displayMax) {
            return true;
        }
    }

    return false;
}

/**
 * Determine whether the value the user effectively entered is a decimal,
 * evaluated in the *displayed* unit (Option B). Used to enforce integer-only
 * fields (range.isDecimal === false).
 *
 * The form converts inputs to metric before validation, so the original typed
 * string (and any "." it contained) is gone — and the raw metric value is a
 * number, so the previous `val.indexOf('.')` check threw on it. Checking the
 * rounded display value for a fractional part both fixes that and applies the
 * rule to what the user actually sees/types (e.g. whole GPM stays whole even
 * though its metric equivalent is fractional).
 */
export function isDisplayValueDecimal(
    metricValue: number,
    unitType: UnitType | null,
    decimals: number = VALIDATION_DISPLAY_DECIMALS
): boolean {
    if (metricValue === undefined || metricValue === null || isNaN(metricValue)) {
        return false;
    }

    const displayValue = roundToDisplayPrecision(metricToDisplay(metricValue, unitType), decimals);
    return !Number.isInteger(displayValue);
}

/**
 * Round a temperature *difference* (delta) to its displayed representation, for
 * comparison against delta thresholds (Option B). A delta scales by the factor
 * only (no +32 offset). Comparing rounded display deltas avoids floating-point
 * false positives from the US -> metric round-trip, consistently with
 * isOutOfDisplayRange for absolute field bounds.
 */
export function roundedDisplayDelta(metricDelta: number, decimals: number = VALIDATION_DISPLAY_DECIMALS): number {
    const isUS = getPreferredUnits().preferredUnits === 'us';
    const display = isUS ? metricDelta * CELSIUS_TO_FAHRENHEIT_FACTOR : metricDelta;
    return roundToDisplayPrecision(display, decimals);
}

export function formatUnitValue(metricValue: number | string | undefined | null, unitType: UnitType | null, round: boolean = true): string {
    if (metricValue === undefined || metricValue === null || metricValue === '') {
        return '-';
    }

    const numVal = typeof metricValue === 'string' ? parseFloat(metricValue) : metricValue;
    if (isNaN(numVal)) return metricValue.toString();

    // Dimensionless: no conversion and no unit suffix. The caller owns the
    // label, because it comes from configuration rather than from UnitType.
    if (unitType === null) {
        return `${round ? Math.round(numVal) : parseFloat(numVal.toFixed(2))}`;
    }

    const isUS = getPreferredUnits().preferredUnits === 'us';

    if (!isUS) {
        let unitStr = '';
        switch (unitType) {
            case 'temperature': unitStr = '°C'; break;
            case 'massFlow': unitStr = 'kg/h'; break;
            case 'volumetricFlow': unitStr = 'm3/h'; break;
            case 'pressure': unitStr = 'kPa'; break;
            case 'velocity': unitStr = 'm/s'; break;
        }
        return `${round ? Math.round(numVal) : parseFloat(numVal.toFixed(2))} ${unitStr}`;
    }

    let converted = numVal;
    let unitStr = '';
    switch (unitType) {
        case 'temperature':
            converted = convertCelsiusToFahrenheit(numVal, round);
            unitStr = '°F';
            break;
        case 'massFlow':
            converted = convertKilogramsToPounds(numVal, round);
            unitStr = 'lb/h';
            break;
        case 'volumetricFlow':
            converted = convertM3hToGpm(numVal, round);
            unitStr = 'GPM';
            break;
        case 'pressure':
            converted = convertKpaToPsi(numVal, round);
            unitStr = 'PSI';
            break;
        case 'velocity':
            converted = convertMsToFps(numVal, round);
            unitStr = 'ft/s';
            break;
    }

    return `${round ? Math.round(converted) : parseFloat(converted.toFixed(2))} ${unitStr}`;
}

/**
 * Format a temperature *difference* (delta) for display.
 *
 * A delta is NOT an absolute temperature: it scales by the conversion factor
 * only, with no +32 offset. A 5 °C difference is a 9 °F difference (not 41 °F).
 * Use this for temperature approach / delta values; use formatUnitValue for
 * absolute temperatures.
 */
export function formatTemperatureDelta(metricDelta: number | string | undefined | null, round: boolean = false): string {
    if (metricDelta === undefined || metricDelta === null || metricDelta === '') {
        return '-';
    }

    const numVal = typeof metricDelta === 'string' ? parseFloat(metricDelta) : metricDelta;
    if (isNaN(numVal)) return metricDelta.toString();

    const isUS = getPreferredUnits().preferredUnits === 'us';
    const converted = isUS ? numVal * CELSIUS_TO_FAHRENHEIT_FACTOR : numVal;
    const unitStr = isUS ? '°F' : '°C';

    return `${round ? Math.round(converted) : parseFloat(converted.toFixed(2))} ${unitStr}`;
}

export function getFormattedDate(input: string) {
    let d = new Date(input);
    return `${d.toLocaleDateString('utc', { year: 'numeric', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}